import React, { useCallback, useMemo } from "react";
import { Tooltip } from "@blueprintjs/core";
import { PopoverPosition } from "@blueprintjs/core";
import WhiskerBar from "../WhiskerBar";
import AlternativeFlowValue from "./components/AlternativeFlowValue";
import { CellState, ResultElementParameterType, SimulationRunState, TableCellResultType, TableElementColumnViewType, TableElementSourceType, } from "store/rs-workspace/rs-workspace.types";
import { N_A } from "shared/rsDictionary";
import { getNumberFromNumberString, renderNumberValue } from "utils/string";
import { formatCellModelParameterValue } from "store/cell-model/cell-model.utils";
import { getTotalRuns } from "store/rs-workspace/utils/calculations.utils";
import { isNotEmpty } from "utils/validate";
import ErrorIcon from "icons/info_triangle.svg";
import * as styles from "./styles.module.scss";
const MAX_VISIBLE_ERRORS = 1;
const TableCell = ({ column, row, index, rowResult, rowCalculationIsInProgress, impossibleMessage = "", setShowCopiedToClipboardMessage, }) => {
    const result = useMemo(() => rowResult?.columns?.find(({ columnId }) => columnId === column.id) ||
        {}, [column.id, rowResult?.columns]);
    const isDataBiased = useMemo(() => result?.errorCount > 0, [result.errorCount]);
    const totalRunsCount = useMemo(() => !rowResult?.accuracy
        ? 0
        : getTotalRuns({ ...rowResult?.accuracy, numberOfOptimisationSteps: null }, row.sourceType === TableElementSourceType.PROCESS_DATA), [rowResult?.accuracy, row.sourceType]);
    const allRunsFailed = useMemo(() => result?.errorCount > 0 && result?.errorCount === totalRunsCount, [result?.errorCount, totalRunsCount]);
    const isNa = useMemo(() => result?.state === CellState.NA, [result?.state]);
    const isError = useMemo(() => {
        const noValueAvailable = result.type === TableCellResultType.SINGLE_WHISKER_BAR
            ? !isNotEmpty(result.bar)
            : !isNotEmpty(result.value);
        return (result?.state === CellState.ERROR ||
            rowResult?.state === SimulationRunState.ERROR ||
            (allRunsFailed && noValueAvailable));
    }, [result, rowResult?.state, allRunsFailed]);
    const failedRunsPercentage = useMemo(() => Math.round((result?.errorCount / totalRunsCount) * 100), [totalRunsCount, result?.errorCount]);
    const emptyContent = useMemo(() => React.createElement("p", { key: index }), [index]);
    const whiskerBarValue = useMemo(() => {
        switch (result.type) {
            case TableCellResultType.SINGLE_WHISKER_BAR:
                switch (column.view) {
                    case TableElementColumnViewType.MOST_LIKELY:
                        return result?.bar?.median;
                    case TableElementColumnViewType.AVERAGE:
                        return result?.bar?.mean;
                    case TableElementColumnViewType.OPTIMISTIC:
                        return result?.bar?.thirdQuartile;
                    case TableElementColumnViewType.PESSIMISTIC:
                        return result?.bar?.firstQuartile;
                    case TableElementColumnViewType.SPREAD:
                        if (result?.bar?.thirdQuartile && result?.bar?.firstQuartile) {
                            return result?.bar?.thirdQuartile - result?.bar?.firstQuartile;
                        }
                        return;
                }
        }
    }, [column.view, result]);
    const renderWhiskerBar = useCallback((bar, whiskerBarValue) => (React.createElement(WhiskerBar, { onCopy: () => setShowCopiedToClipboardMessage(true), bar: bar, row: row, columnParameter: column.parameter, value: whiskerBarValue })), [column.parameter, row, setShowCopiedToClipboardMessage]);
    const renderBiasedDataTooltip = useCallback(() => (React.createElement(Tooltip, { minimal: true, openOnTargetFocus: false, className: styles.tooltipRoot, portalClassName: styles.tooltipPortal, popoverClassName: styles.tooltip, position: PopoverPosition.BOTTOM, rootBoundary: "viewport", content: React.createElement(React.Fragment, null,
            React.createElement("p", { className: styles["tooltip-title"] }, "Data is biased"),
            React.createElement("p", { className: styles["tooltip-subtitle"] },
                result?.errorCount,
                " out of ",
                totalRunsCount,
                " failed"),
            React.createElement("p", { className: styles["tooltip-list"] }, result.errors?.slice(0, MAX_VISIBLE_ERRORS).map((text, i) => (React.createElement("span", { key: `${i}-${text}`, className: styles["tooltip-listItem"] }, text))))), transitionDuration: 0 },
        React.createElement("div", { className: styles.biasedPreview },
            failedRunsPercentage,
            "% ",
            React.createElement(ErrorIcon, { className: styles.icon })))), [result.errors, result?.errorCount, totalRunsCount, failedRunsPercentage]);
    if (row.changed || column.changed || rowCalculationIsInProgress || rowResult?.fetching) {
        return emptyContent;
    }
    if (isNa) {
        return (React.createElement(Tooltip, { minimal: true, content: impossibleMessage || "No results", portalClassName: styles.tooltip, className: isNa ? styles["result-na"] : styles["result-missing"], placement: "bottom", boundary: "clippingParents", openOnTargetFocus: false }, N_A));
    }
    if (isError) {
        return (React.createElement("div", { className: styles.errorMessage },
            React.createElement(Tooltip, { minimal: true, content: result?.errors?.[0] || rowResult?.error || "Failed to get result", portalClassName: styles.tooltip, placement: "bottom", boundary: "clippingParents", openOnTargetFocus: false },
                React.createElement(ErrorIcon, { className: styles["error-icon"] }))));
    }
    let content = emptyContent;
    if (result.state === CellState.VALUE) {
        switch (result.type) {
            case TableCellResultType.STATIC:
                content = (React.createElement(React.Fragment, null,
                    React.createElement("p", { className: styles["result-value"] }, column.parameter.type === ResultElementParameterType.ALTERNATIVE_FLOW ? (React.createElement(AlternativeFlowValue, { value: result.value, column: column })) : !!result?.value && isNaN(getNumberFromNumberString(result.value)) ? (formatCellModelParameterValue(result.value)) : (renderNumberValue(Number(result?.value))))));
                break;
            case TableCellResultType.SINGLE_WHISKER_BAR:
                content = result.bar ? renderWhiskerBar(result.bar, whiskerBarValue) : emptyContent;
                break;
        }
    }
    return (React.createElement("div", { className: styles.cell },
        isDataBiased && renderBiasedDataTooltip(),
        content));
};
export default TableCell;
